import { ItemCategoryService } from 'src/app/EBMSuite/services/item-categories/item-category.service';
import {
  Component,
  EventEmitter,
  InjectionToken,
  Injector,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormlyFormOptions, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import {
  EItemType,
  EResourceRates,
  ICategory,
  IItem
} from 'src/app/EBMSuite/models/item.model';
import { ItemsService } from 'src/app/EBMSuite/services/items/items.service';
import { FormlyPrimeNGModule } from '@ngx-formly/primeng';
import { ButtonModule } from 'primeng/button';
import { MessagesModule } from 'primeng/messages';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { UtilService } from 'src/app/common/services/util/util.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteSearchType } from 'src/app/common/model/util.model';
import { CommonModule } from '@angular/common';

const DYNAMIC_DIALOG_REF = new InjectionToken<DynamicDialogRef>('dynamic-dialog-ref');
const DYNAMIC_DIALOG_CONFIG = new InjectionToken<DynamicDialogConfig>(
  'dynamic-dialog-config'
);

@Component({
  selector: 'app-add-item',
  templateUrl: './add-item.component.html',
  styleUrls: ['./add-item.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    FormlyModule,
    FormlyPrimeNGModule,
    MessagesModule,
    ButtonModule,
    AutoCompleteModule,
    ToastModule,
    CommonModule
  ],
  providers: [
    MessageService,
    {
      provide: DYNAMIC_DIALOG_REF,
      useFactory: (utilService: UtilService) => {
        if (!utilService.doesURLHaveText('invoice')) {
          return new DynamicDialogRef();
        } else {
          return null;
        }
      },
      deps: [UtilService]
    },
    {
      provide: DYNAMIC_DIALOG_CONFIG,
      useFactory: (utilService: UtilService) => {
        if (!utilService.doesURLHaveText('invoice')) {
          return new DynamicDialogConfig();
        } else {
          return null;
        }
      },
      deps: [UtilService]
    }
  ]
})
export class AddItemComponent implements OnInit {
  private dialogRef!: DynamicDialogRef;

  subscription = new Subscription();
  suggestionsCategories: ICategory[] = [];
  allCategories: ICategory[] = [];
  isEdit = false;
  errorMessage = '';
  itemEdit!: IItem;
  addItemForm = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};
  fields!: FormlyFieldConfig[];

  isSubmiting = false;
  closeResult = '';
  @Output() itemAdded: EventEmitter<IItem> = new EventEmitter<IItem>();

  constructor(
    private itemsService: ItemsService,
    private activatedRoute: ActivatedRoute,
    public utilService: UtilService,
    private itemCategoryService: ItemCategoryService,
    private injector: Injector,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.getItemIdFromParam();
    setTimeout(() => {
      this.getFormFields();
    }, 0);
  }

  getFormFields(): void {
    this.fields = [
      {
        className: 'col-12',
        key: 'itemType',
        type: 'select-with-description',
        props: {
          label: 'Item Type *',
          description:
            'Goods are tangible products that can be physically possessed or touched e.g Watch, Bread',
          placeholder: 'Select item type',
          disabled: this.isEdit,
          options: Object.values(EItemType).map((x) => {
            return {
              label:
                x == EItemType.INVENTORYITEM
                  ? 'Goods'
                  : x == EItemType.SERVICEITEM
                    ? 'Service'
                    : 'Bookable',
              value: x
            };
          }),
          required: true
        }
      },
      {
        fieldGroupClassName: 'grid',
        id: 'nameWrapper',
        fieldGroup: [
          {
            className: 'sm:col-6 col-12',
            key: 'name',
            type: 'input',
            props: {
              label: 'Item Name',
              placeholder: 'Enter Item Name',
              required: true
            }
          },
          {
            className: 'sm:col-6 col-12',
            key: 'sku',
            type: 'input',
            props: {
              label: 'Item SKU (Stock Keeping Unit)',
              placeholder: 'Enter Item SKU'
            }
          }
        ]
      },
      {
        fieldGroupClassName: 'grid flex align-items-center',
        fieldGroup: [
          {
            className: 'sm:col-5 col-12 ',
            key: 'itemCategory',
            type: 'autoComplete',
            props: {
              label: 'Category',
              placeholder:
                'Type to Search for a category or create a new one by typing in this field...',
              searchConfig: {
                searchType: AutoCompleteSearchType.ITEMCATEGORIES,
                fieldName: 'name',
                fieldValue: '_id',
                multiple: false,
                forceSelection: false
              }
            }
          },
          {
            className: 'sm:col-5 col-12',
            key: 'taxes',
            type: 'selectItemTaxes',
            props: {
              required: false
            }
          },
          {
            className: 'sm:col-2 col-12 pl-0 sm:pl-4 ',
            key: 'trackInventory',
            type: 'checkbox',
            defaultValue: true,
            props: {
              label: 'Track Inventory',
              disabled: this.isEdit
            }
          }
        ]
      },
      {
        fieldGroupClassName: 'grid',
        fieldGroup: [
          {
            className: 'sm:col-4 col-12',
            key: 'quantity',
            type: 'input',
            defaultValue: 0,
            props: {
              label: 'Initial Quantity',
              type: 'number',
              min: 0,
              placeholder: 'Enter initial quantity on hand'
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'reorderLevel',
            type: 'input',
            defaultValue: 0,
            props: {
              label: 'Reorder Level',
              type: 'number',
              min: 0,
              placeholder: 'Enter the point of reorder'
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'unitOfMeasure',
            type: 'input',
            props: {
              label: 'Unit of Measure',
              placeholder: 'Enter item unit of measure'
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'cost',
            type: 'input',
            props: {
              label: 'Cost (Purchase cost of one unit)',
              type: 'number',
              min: this.model.price,
              placeholder: 'Enter purchase cost of one unit',
              required: true
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'price',
            type: 'input',
            props: {
              label: 'Price (Selling price for one unit)',
              type: 'number',
              min: 1,
              placeholder: 'Enter selling price for one unit',
              required: true
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'pricingRate',
            type: 'select-with-description',
            props: {
              label: 'Rate *',
              description: 'Rate the price is based upon',
              placeholder: 'Select a rate',

              options: Object.values(EResourceRates).map((x) => {
                return {
                  label: x,
                  value: x
                };
              }),
              required: true
            }
          },
          {
            className: 'sm:col-4 col-12',
            key: 'profitMargin',
            type: 'input',
            props: {
              label: 'Profit Margin',
              type: 'number',
              readonly: true
            }
          }
        ]
      },
      {
        className: 'col-12',
        key: 'description',
        type: 'input',
        props: {
          label: 'Description',
          placeholder: 'Enter item descripton'
        }
      }
    ];
  }

  getAllCategories(): void {
    this.subscription.add(
      this.itemCategoryService.getCategories().subscribe({
        next: (res) => {
          this.allCategories = res.result;
        }
      })
    );
  }
  filterCategories(event: AutoCompleteCompleteEvent) {
    const filtered: any[] = [];
    const query = event.query;

    for (let i = 0; i < (this.allCategories as any[]).length; i++) {
      const category = (this.allCategories as any[])[i];
      if (category.name.toLowerCase().indexOf(query.toLowerCase()) !== -1) {
        filtered.push(category);
      }
    }
    this.suggestionsCategories = filtered;
  }

  createOrUpdateItem(form: any): void {
    this.isSubmiting = true;

    let item = {};
    if (typeof form.value.itemCategory !== 'object') {
      const { itemCategory, ...rest } = form.value;
      item = {
        ...rest,
        categoryToAdd: form.value.itemCategory
      };
    } else {
      item = {
        ...form.value,
        itemCategory: form.value.itemCategory
      };
    }

    const itemId = this.isEdit ? this.itemEdit._id : '';
    const submitURL = this.isEdit
      ? this.itemsService.editItem(itemId, item)
      : this.itemsService.addItem(item);
    submitURL.subscribe({
      next: (res) => {
        this.addItemForm.reset();
        if (this.utilService.doesURLHaveText('item')) {
          this.utilService.goBack();
        } else if (this.utilService.doesURLHaveText('onboarding')) {
        } else {
          this.dialogRef = this.injector.get(DynamicDialogRef);
          this.dialogRef.close(res.result);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Item ${this.isEdit ? 'edited' : 'added'} successfully`
        });
        this.itemAdded.emit(res.result);
        this.isSubmiting = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.errors?.message
            ? err?.error?.errors?.message
            : 'Please try again'
        });
        this.isSubmiting = false;
      }
    });
  }
  getItemIdFromParam(): void {
    this.subscription.add(
      this.activatedRoute.params.subscribe({
        next: (param) => {
          const itemId = param['itemId'];
          if (itemId) {
            this.isEdit = true;
            this.getItemDetails(itemId);
          } else {
            this.model = {
              ...this.model,
              itemType: EItemType.INVENTORYITEM
            };
          }
        }
      })
    );
  }

  getItemDetails(itemId: string): void {
    this.subscription.add(
      this.itemsService.getItem(itemId).subscribe((res) => {
        this.itemEdit = res.result;
        this.model = {
          ...this.itemEdit,
          reorderLevel: this.itemEdit.reorderLevel ? this.itemEdit.reorderLevel : 0,
          taxes: this.itemEdit.taxes?.map((x) => x._id)
        };
        this.modelChange();
        this.disableItemTypeFieldOnEdit();
      })
    );
  }

  disableItemTypeFieldOnEdit(): void {
    const itemType = this.fields[0].fieldGroup
      ? this.fields[0].fieldGroup.find((x) => x.key === 'itemType')
      : ({} as FormlyFieldConfig);

    itemType?.props ? (itemType.props.disabled = this.isEdit) : null;
  }

  modelChange(): void {
    // Fetch the field groups
    const trackInventoryField =
      this.fields[2]?.fieldGroup?.find((x) => x.key === 'trackInventory') || {};
    const trackInventoryFieldIndex =
      this.fields[2]?.fieldGroup?.findIndex((x) => x.key === 'trackInventory') ?? -1;

    const quantityField =
      this.fields[3]?.fieldGroup?.find((x) => x.key === 'quantity') || {};
    const quantityFieldIndex =
      this.fields[3]?.fieldGroup?.findIndex((x) => x.key === 'quantity') ?? -1;

    const costField = this.fields[3]?.fieldGroup?.find((x) => x.key === 'cost') || {};
    const costFieldIndex =
      this.fields[3]?.fieldGroup?.findIndex((x) => x.key === 'cost') ?? -1;

    const reorderLevelField =
      this.fields[3]?.fieldGroup?.find((x) => x.key === 'reorderLevel') || {};
    const reorderLevelFieldIndex =
      this.fields[3]?.fieldGroup?.findIndex((x) => x.key === 'reorderLevel') ?? -1;

    const profitMarginField =
      this.fields[3]?.fieldGroup?.find((x) => x.key === 'profitMargin') || {};
    const profitMarginFieldIndex =
      this.fields[3]?.fieldGroup?.findIndex((x) => x.key === 'profitMargin') ?? -1;

    const pricingRateField =
      this.fields[3]?.fieldGroup?.find((x) => x.key === 'pricingRate') || {};
    const pricingRateFieldIndex =
      this.fields[3]?.fieldGroup?.findIndex((x) => x.key === 'pricingRate') ?? -1;

    // Update model properties
    // this.model.trackInventory = this.model.itemType === EItemType.INVENTORYITEM ? this.model.trackInventory : false;

    // Update visibility of fields
    trackInventoryField.hide = this.model.itemType !== EItemType.INVENTORYITEM;
    quantityField.hide =
      this.model.itemType !== EItemType.INVENTORYITEM || !this.model.trackInventory;
    costField.hide = this.model.itemType !== EItemType.INVENTORYITEM;
    reorderLevelField.hide =
      this.model.itemType !== EItemType.INVENTORYITEM || !this.model.trackInventory;
    profitMarginField.hide =
      this.model.itemType === EItemType.SERVICEITEM ||
      this.model.itemType === EItemType.BOOKABLERESOURCE;
    pricingRateField.hide =
      this.model.itemType === EItemType.SERVICEITEM ||
      this.model.itemType === EItemType.INVENTORYITEM;

    // Update required status for cost field
    if (costField.props) {
      costField.props.required = this.model.trackInventory;
    }

    // Assign updated fields back to fieldGroup
    if (this.fields[3]?.fieldGroup && this.fields[2]?.fieldGroup) {
      this.fields[2].fieldGroup[trackInventoryFieldIndex] = trackInventoryField;
      this.fields[3].fieldGroup[quantityFieldIndex] = quantityField;
      this.fields[3].fieldGroup[costFieldIndex] = costField;
      this.fields[3].fieldGroup[reorderLevelFieldIndex] = reorderLevelField;
      this.fields[3].fieldGroup[profitMarginFieldIndex] =
        profitMarginField as FormlyFieldConfig;
      this.fields[3].fieldGroup[pricingRateFieldIndex] =
        pricingRateField as FormlyFieldConfig;
    }

    // Update description based on item type
    const itemTypeField =
      this.fields.find((x) => x.key === 'itemType') || ({} as FormlyFieldConfig);
    const itemTypeFieldIndex = this.fields.findIndex((x) => x.key === 'itemType') ?? -1;

    if (itemTypeField?.props) {
      itemTypeField.props.description =
        this.model.itemType === EItemType.INVENTORYITEM
          ? 'These are items that can be physically possessed or touched e.g. Watch, Bread'
          : this.model.itemType === EItemType.SERVICEITEM
            ? 'These are items provided to fulfill specific needs or desires of customers e.g. Haircut, Carwash '
            : this.model.itemType === EItemType.BOOKABLERESOURCE
              ? 'These are items that can be reserved or scheduled for a specific time or duration e.g. Hotel room, Car for hire'
              : '';
    }
    this.fields[itemTypeFieldIndex] = itemTypeField;

    // Recalculate profit margin
    this.calculateProfitMargin(this.model.cost, this.model.price);
  }

  calculateProfitMargin(cost: number, price: number) {
    if (cost && price) {
      const profit = price - cost;
      this.model = {
        ...this.model,
        profitMargin: profit
      };
    }
  }

  getCategories(): ICategory[] {
    this.itemCategoryService.getCategories().subscribe((res) => {
      this.allCategories = res.result;
    });
    return this.allCategories;
  }

  triggerCancel(): void {
    if (this.utilService.doesURLHaveText('items')) {
      this.utilService.goBack();
    } else {
      this.dialogRef = this.injector.get(DynamicDialogRef);
      this.dialogRef.close();
    }
  }
}
